import {
  Zoom,
  Paper,
  Box,
  Button,
  List,
  Link,
  alpha,
  Theme,
  makeStyles,
  IconButton,
  Typography,
} from '@material-ui/core';
import { PlayArrow } from '@material-ui/icons';
import clsx from 'clsx';
import Image from 'material-ui-image';

import ImageViewer from 'components/ImageViewer';
import InfiniteScroll from 'components/InfiniteScroll';
import { MidiasContatoInterface } from 'store/reducers/contatos';

type StylesProps = {
  midiaItemHeight?: number;
  midiaItemWidth?: number;
  columns?: number;
};

const useStyles = makeStyles<Theme, StylesProps>(
  ({ spacing, shadows, palette }) => ({
    list: {
      height: '100%',
      overflow: 'auto',
    },
    content: {
      overflow: 'hidden',
      padding: spacing(2),
    },
    paper: {
      display: 'flex',
      position: 'relative',
      alignItems: 'center',
      justifyContent: 'center',
      padding: spacing(1) / 2,
      width: '100%',
      aspectRatio: '1 / 1',
      backgroundColor: palette.background.tertiary,

      '&:hover': {
        boxShadow: shadows[6],
      },
    },
    activeMidia: {
      backgroundColor: palette.primary.main,
    },
    contentItem: {
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      position: 'relative',
      minWidth: ({ midiaItemHeight }) => midiaItemHeight || spacing(10),
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: ({ columns }) => `repeat(${columns || 5}, 1fr)`,
      gap: spacing(1),
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      borderRadius: spacing(1),
    },
    iconButton: {
      color: palette.getContrastText(alpha(palette.common.black, 0.4)),
      backgroundColor: `${alpha(palette.common.black, 0.4)} !important`,
      position: 'absolute',
      top: '35%',
      left: '35%',
    },
    background: {
      borderRadius: spacing(1),
      color: palette.common.white,
      backgroundColor: alpha(palette.common.black, 0.68),
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      position: 'absolute',
      bottom: 0,
      top: 0,
      left: 0,
      right: 0,
    },
  }),
);

interface ListaDeMidiasProps {
  midias: MidiasContatoInterface[];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => void;
  midiaItemHeight?: number;
  midiaItemWidth?: number;
  columns?: number;
}

export default function ListaMidiasContato(props: ListaDeMidiasProps) {
  const {
    midias,
    loading,
    hasMore,
    loadMore,
    midiaItemHeight,
    midiaItemWidth,
    columns,
  } = props;

  const classes = useStyles({ midiaItemHeight, midiaItemWidth, columns });

  const renderMidia = (midia: MidiasContatoInterface) => {
    const { id, type, url, title } = midia;
    switch (type) {
      case 'IMAGEM':
        return (
          <Zoom in mountOnEnter unmountOnExit timeout={400}>
            <Paper elevation={1} className={clsx(classes.paper)}>
              <Box className={classes.contentItem}>
                <ImageViewer src={url}>
                  <Image
                    cover
                    loading={false}
                    src={url || ''}
                    className={classes.image}
                    style={{ backgroundColor: 'inherit' }}
                  />
                </ImageViewer>
              </Box>
            </Paper>
          </Zoom>
        );
      case 'VIDEO':
        return (
          <Zoom in mountOnEnter unmountOnExit timeout={400}>
            <Paper elevation={1} className={clsx(classes.paper)}>
              <Link
                component={Button}
                style={{ display: 'flex', padding: 0 }}
                key={id}
                rel="noopener noreferrer"
                title={title}
                target="_blank"
                href={url}
              >
                <Box className={classes.contentItem}>
                  <Image
                    cover
                    className={classes.image}
                    src={url || ''}
                    alt={title || ''}
                    loading={false}
                    style={{ backgroundColor: 'inherit' }}
                  />
                  <IconButton size="small" className={classes.iconButton}>
                    <PlayArrow color="inherit" />
                  </IconButton>
                </Box>
              </Link>
            </Paper>
          </Zoom>
        );
      default:
        return null;
    }
  };

  const renderMidiasContato = () => {
    return (
      <div className={classes.content}>
        <div className={classes.gridContainer}>
          {midias.map(midia => renderMidia(midia))}
        </div>
      </div>
    );
  };
  return (
    <List id="scrollableMidiasContato" className={classes.list}>
      <InfiniteScroll
        data={midias}
        next={loadMore}
        scrollableTarget="scrollableMidiasContato"
        hasMore={hasMore}
        loading={loading}
      >
        {!loading && midias.length === 0 && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height={80}
          >
            <Typography variant="body2" color="textSecondary">
              Nenhuma mídia foi encontrada.
            </Typography>
          </Box>
        )}

        {renderMidiasContato()}
      </InfiniteScroll>
    </List>
  );
}
